import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Video modal. Supports YouTube and Vimeo embeds.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   video: { provider: 'youtube' | 'vimeo', id: string, title?: string, hash?: string }
 */
export const VideoModal = ({ open, onClose, video }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !video) return null;

  let src = "";
  if (video.provider === "youtube") {
    src = `https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`;
  } else if (video.provider === "vimeo") {
    const h = video.hash ? `?h=${video.hash}&autoplay=1` : "?autoplay=1";
    src = `https://player.vimeo.com/video/${video.id}${h}`;
  }

  return (
    <div
      data-testid="video-modal"
      role="dialog"
      aria-modal="true"
      aria-label={video.title || "Video player"}
      className="modal-backdrop fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button
        data-testid="video-modal-close"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-5 right-5 md:top-8 md:right-8 w-10 h-10 flex items-center justify-center border border-white/30 text-white/80 hover:text-white hover:border-white transition-colors"
      >
        <X size={16} strokeWidth={1.2} />
      </button>

      <div
        className="relative w-full max-w-6xl aspect-video bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={src}
          title={video.title || "Video"}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>

      {video.title && (
        <div className="absolute bottom-6 left-0 right-0 text-center text-white/75 font-sans text-[11px] uppercase tracking-[0.32em] font-light">
          {video.title}
        </div>
      )}
    </div>
  );
};
